import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import postLimitService from '../services/postLimitService';
import { PostIcon, LimitIcon, LimitWarningIcon, UnlimitedIcon } from './icons';

interface PostLimitInfo {
  current: number;
  limit: number;
  remaining: number;
  resetDate: Date;
  isUnlimited: boolean;
  tier: string;
}

interface PostLimitIndicatorProps {
  showDetails?: boolean;
  className?: string;
}

const PostLimitIndicator: React.FC<PostLimitIndicatorProps> = ({ 
  showDetails = false, 
  className = '' 
}) => {
  const [limitInfo, setLimitInfo] = useState<PostLimitInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    const loadLimitInfo = async () => {
      try {
        if (auth.currentUser) {
          const info = await postLimitService.getPostLimitInfo(auth.currentUser.uid);
          setLimitInfo(info);
        }
      } catch (error) {
        console.error('Error loading post limit info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLimitInfo();
  }, []);

  if (loading || !limitInfo) {
    return null;
  }

  if (limitInfo.isUnlimited) {
    return (
      <div className={`text-green-600 text-sm flex items-center gap-1.5 ${className}`}>
        <UnlimitedIcon size={16} weight="regular" /> Onbeperkt posten (Premium)
      </div>
    );
  }

  const percentage = (limitInfo.current / limitInfo.limit) * 100;
  const isNearLimit = percentage >= 80;
  const isAtLimit = limitInfo.remaining === 0;

  const getStatusColor = () => {
    if (isAtLimit) return 'text-red-600';
    if (isNearLimit) return 'text-orange-600';
    return 'text-gray-600';
  };

  const StatusIcon = () => {
    if (isAtLimit) return <LimitIcon size={16} weight="regular" />;
    if (isNearLimit) return <LimitWarningIcon size={16} weight="regular" />;
    return <PostIcon size={16} weight="regular" />;
  };

  return (
    <div className={`${getStatusColor()} text-sm ${className}`}>
      <div className="flex items-center gap-2">
        <StatusIcon />
        <span>
          {limitInfo.current}/{limitInfo.limit} posts deze maand
        </span>
        {showDetails && (
          <span className="text-xs text-gray-500">
            ({limitInfo.remaining} over)
          </span>
        )}
      </div>
      
      {showDetails && (
        <div className="mt-1">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-orange-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Reset op {limitInfo.resetDate.toLocaleDateString('nl-NL')}
          </div>
        </div>
      )}

      {isAtLimit && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
          <strong>Post limiet bereikt!</strong><br />
          Upgrade naar Premium voor onbeperkt posten, of wacht tot de maandelijkse reset.
        </div>
      )}

      {isNearLimit && !isAtLimit && (
        <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-orange-700 text-xs">
          <strong>Bijna bij je limiet!</strong><br />
          Je hebt nog {limitInfo.remaining} posts over deze maand.
        </div>
      )}
    </div>
  );
};

export default PostLimitIndicator;
